import React, { useState, useEffect, useMemo, ChangeEvent } from 'react';
import { Plus } from 'lucide-react';
import { deleteContact } from 'services/api/ContactServiceAPI';
import { Select, Button, SearchBar } from 'common/components';
import { Modal } from 'common/components/Modal';
import AlertModal from 'components/AlertModal/AlertModal';
import UploadContactsModal from 'components/UploadContactsModal';
import ContactDrawer from './ContactDrawer';
import ContactsTable from './ContactsTable';
import { Contact, GetContactsParams, Workflow } from 'types/contact.type';
import styles from './ContactManager.module.scss';
import { useGetContacts } from 'services/hooks/queries/useGetContacts';

// Component-specific types
interface SelectOption {
    value: string;
    label: string;
    disabled?: boolean;
}

type AlertType = 'success' | 'error' | 'warning' | 'info';

const sortOptions: SelectOption[] = [
    { value: '', label: 'Sort by', disabled: true },
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'name', label: 'Name (A-Z)' },
];

const ContactManager: React.FC = () => {
    // State for contacts data
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [rawStatusOptions, setRawStatusOptions] = useState<string[]>([]);
    const [workflows, setWorkflows] = useState<Workflow[]>([]);
    const [selectedWorkflow, setSelectedWorkflow] = useState<string>('');

    // State for filters
    const [selectedStatus, setSelectedStatus] = useState<string>('All Statuses');
    const [sortBy, setSortBy] = useState<GetContactsParams['sortBy']>('newest');
    const [searchTerm, setSearchTerm] = useState<string>('');

    // State for upload modal
    const [isUploadModalOpen, setIsUploadModalOpen] = useState<boolean>(false);

    // Pagination states
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [rowsPerPage, setRowsPerPage] = useState<number>(10);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [totalRecords, setTotalRecords] = useState<number>(0);

    // State for multiple selection
    const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    // State for contact drawer
    const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState('details');

    // State for alert modal
    const [showAlertModal, setShowAlertModal] = useState<boolean>(false);
    const [alertMessage, setAlertMessage] = useState<string>('');
    const [alertType, setAlertType] = useState<AlertType>('info');

    // Confirmation modal state
    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
    const [confirmMessage, setConfirmMessage] = useState<string>('');
    const [confirmAction, setConfirmAction] = useState<(() => Promise<void>) | null>(null);

    // Load contacts on component mount and when filters change
    const { contactsData, isContactsLoading, refetchContactsData } = useGetContacts({
        page: currentPage,
        limit: rowsPerPage,
        query: searchTerm,
        status: selectedStatus,
        workflowId: selectedWorkflow,
        sortBy: sortBy,
    });

    useEffect(() => {
        if (contactsData) {
            setContacts(contactsData.contacts);

            if (contactsData.pagination) {
                setTotalPages(contactsData.pagination.totalPages || 1);
                setTotalRecords(contactsData.pagination.totalContacts || 0);

                if (contactsData.pagination.page && contactsData.pagination.page !== currentPage) {
                    setCurrentPage(contactsData.pagination.page);
                }
            }

            if (contactsData.statuses?.length && rawStatusOptions.length === 0) {
                setRawStatusOptions(contactsData.statuses);
            }

            if (contactsData.workflows?.length && workflows.length === 0) {
                setWorkflows(contactsData.workflows);
            }
        }
    }, [contactsData]);

    // Auto-adjust page if current page exceeds total pages
    useEffect(() => {
        if (totalPages > 0 && currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [totalPages, currentPage]);

    // Check if all contacts on current page are selected
    const areAllSelected = useMemo(() => {
        return contacts.length > 0 && contacts.every((contact) => selectedContacts.includes(contact.contactId));
    }, [contacts, selectedContacts]);

    // Handle search (called by SearchBar component with debounced value)
    const handleSearch = (value: string) => {
        setSearchTerm(value);
        setCurrentPage(1);
    };

    // Clear search and filters (called by SearchBar clear button)
    const handleClearSearch = () => {
        setSelectedStatus('All Statuses');
        setSortBy(undefined);
        setSelectedContacts([]);
        setCurrentPage(1);
    };

    // Handle workflow filter change
    const handleWorkflowFilterChange = (e: ChangeEvent<HTMLSelectElement>) => {
        setSelectedWorkflow(e.target.value);
        setCurrentPage(1);
    };

    // Handle sort change
    const handleSortChange = (e: ChangeEvent<HTMLSelectElement>) => {
        setSortBy(e.target.value as GetContactsParams['sortBy']);
        setCurrentPage(1);
    };

    // Handle checkbox change for single contact
    const handleContactSelect = (contactId: string) => {
        setSelectedContacts((prev) => {
            if (prev.includes(contactId)) {
                return prev.filter((id) => id !== contactId);
            } else {
                return [...prev, contactId];
            }
        });
    };

    // Handle "select all" checkbox change
    const handleSelectAll = () => {
        if (areAllSelected) {
            setSelectedContacts((prev) => prev.filter((id) => !contacts.some((contact) => contact.contactId === id)));
        } else {
            const currentPageIds = contacts.map((contact) => contact.contactId);
            setSelectedContacts((prev) => {
                const newSelection = [...prev];
                currentPageIds.forEach((id) => {
                    if (!newSelection.includes(id)) {
                        newSelection.push(id);
                    }
                });
                return newSelection;
            });
        }
    };

    // Handle actions dropdown change
    const handleActionsChange = (e: ChangeEvent<HTMLSelectElement>) => {
        const action = e.target.value;
        if (action === 'delete') {
            handleBulkDelete();
            e.target.value = '';
        }
    };

    // Handle bulk delete
    const handleBulkDelete = async () => {
        if (selectedContacts.length === 0) {
            setAlertMessage('Please select at least one contact to delete.');
            setAlertType('error');
            setShowAlertModal(true);
            return;
        }

        setConfirmMessage(`Are you sure you want to delete ${selectedContacts.length} selected contacts?`);
        setConfirmAction(() => async () => {
            try {
                setIsDeleting(true);

                await deleteContact(selectedContacts);

                const updatedContacts = contacts.filter((c) => !selectedContacts.includes(c.contactId));
                setContacts(updatedContacts);

                setSelectedContacts([]);

                setAlertMessage(`${selectedContacts.length} contacts have been deleted successfully.`);
                setAlertType('success');
                setShowAlertModal(true);
            } catch (error) {
                console.error('Error deleting contacts:', error);
                setAlertMessage('Error deleting contacts: ' + ((error as Error).message || 'Unknown error'));
                setAlertType('error');
                setShowAlertModal(true);
            } finally {
                setIsDeleting(false);
            }
        });
        setShowConfirmModal(true);
    };

    // Handle add new contact button click
    const handleAddNewContact = () => {
        setIsUploadModalOpen(true);
    };

    // Handle upload modal close
    const handleUploadModalClose = () => {
        setIsUploadModalOpen(false);
    };

    // Handle successful upload
    const handleUploadSuccess = () => {
        setAlertMessage('Contacts uploaded successfully.');
        setAlertType('success');
        setShowAlertModal(true);
        refetchContactsData();
    };

    // Handle upload error
    const handleUploadError = (message: string) => {
        setAlertMessage(message);
        setAlertType('error');
        setShowAlertModal(true);
    };

    // Handle row click to open contact drawer
    const handleRowClick = async (contact: Contact) => {
        setIsDrawerOpen(true);
        setActiveTab('details');
        setSelectedContact(contact);
    };

    // Close the contact drawer
    const closeDrawer = () => {
        setIsDrawerOpen(false);
    };

    // Handle drawer tab change
    const handleTabChange = (tab: string) => {
        setActiveTab(tab);
    };

    // Pagination handlers for the Pagination component
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handleRowsPerPageChange = (newRowsPerPage: number) => {
        setRowsPerPage(newRowsPerPage);
        setCurrentPage(1);
    };

    // Close the alert modal
    const closeAlertModal = () => {
        setShowAlertModal(false);
        setAlertMessage('');
    };

    // Close the confirmation modal
    const closeConfirmModal = () => {
        setShowConfirmModal(false);
        setConfirmMessage('');
        setConfirmAction(null);
    };

    // Execute the confirm action
    const executeConfirmAction = async () => {
        if (confirmAction) {
            await confirmAction();
        }
        closeConfirmModal();
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <h1 className={styles.title}>Contact Management</h1>
                    <p className={styles.description}>Manage and track your contacts in one place</p>
                </div>
                <Button
                    variant='primary'
                    icon={Plus}
                    iconPosition='left'
                    onClick={handleAddNewContact}
                    className={styles.addButton}
                >
                    Add New Contact
                </Button>
            </div>

            <div className={styles.filters}>
                <SearchBar
                    placeholder='Search contacts by name or email...'
                    onSearch={handleSearch}
                    onClear={handleClearSearch}
                    debounceDelay={300}
                    className={styles.searchBox}
                />

                <div className={styles.filterOptions}>
                    <Select options={sortOptions} value={sortBy} onChange={handleSortChange} />

                    <Select
                        options={[
                            { value: '', label: 'All workflows' },
                            ...workflows.map(({ workflowId, workflowName }) => ({
                                value: workflowId,
                                label: workflowName,
                            })),
                        ]}
                        value={selectedWorkflow}
                        onChange={handleWorkflowFilterChange}
                    />

                    <Select
                        value=''
                        onChange={handleActionsChange}
                        options={[
                            {
                                value: '',
                                disabled: true,
                                label: `Actions ${selectedContacts.length > 0 ? `(${selectedContacts.length})` : ''}`,
                            },
                            { value: 'delete', label: 'Delete Selected', disabled: selectedContacts.length === 0 },
                        ]}
                    />
                </div>
            </div>

            <ContactsTable
                contacts={contacts}
                loading={isContactsLoading}
                selectedContacts={selectedContacts}
                areAllSelected={areAllSelected}
                searchTerm={searchTerm}
                totalRecords={totalRecords}
                currentPage={currentPage}
                totalPages={totalPages}
                rowsPerPage={rowsPerPage}
                onRowClick={handleRowClick}
                onContactSelect={handleContactSelect}
                onSelectAll={handleSelectAll}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
            />

            {/* Upload Modal for CSV Upload */}
            <UploadContactsModal
                isOpen={isUploadModalOpen}
                onClose={handleUploadModalClose}
                workflows={workflows}
                onUploadSuccess={handleUploadSuccess}
                onUploadError={handleUploadError}
            />

            {/* Alert Modal */}
            <AlertModal isOpen={showAlertModal} onClose={closeAlertModal} message={alertMessage} type={alertType} />

            {/* Confirmation Modal */}
            <Modal
                isOpen={showConfirmModal}
                onClose={closeConfirmModal}
                title='Confirm Delete'
                size='sm'
                showDefaultFooter={false}
            >
                <div className={styles.modalBody}>
                    <p className={styles.modalText}>{confirmMessage}</p>
                    <div className={styles.modalFooter}>
                        <Button variant='outline-secondary' onClick={closeConfirmModal}>
                            Cancel
                        </Button>
                        <Button variant='danger' onClick={executeConfirmAction} disabled={isDeleting}>
                            {isDeleting ? 'Deleting...' : 'Delete'}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Contact Details Drawer */}
            <ContactDrawer
                isOpen={isDrawerOpen}
                contact={selectedContact}
                activeTab={activeTab}
                rawStatusOptions={rawStatusOptions}
                onClose={closeDrawer}
                onTabChange={handleTabChange}
            />
        </div>
    );
};

export default ContactManager;
